/** Размер страницы «Все»: Int32.MaxValue — поле take на бэке 32-битное,
 *  большее значение (MAX_SAFE_INTEGER) роняло бы десериализацию запроса. */
export const DATA_TABLE_ALL_PAGE_SIZE = 2147483647;

// Сохранённое состояние живёт дольше версии библиотеки: localStorage, виды на
// сервере, состояние в URL. Поэтому формат — свой, а не типы TanStack: смена
// их формы (v9 перевела закрепление с left/right на start/end) не должна
// превращать уже сохранённое в мусор. Перевод — на границе с таблицей.

/** Уровень сортировки. */
export interface IDataTableSavedSort {
    id: string;
    desc: boolean;
}

/** Фильтр колонки (значение — IDataTableColumnFilter). */
export interface IDataTableSavedFilter {
    id: string;
    value: unknown;
}

/** Закреплённые колонки по сторонам. */
export interface IDataTableSavedPinning {
    left?: string[];
    right?: string[];
}

/** Персистентная часть состояния грида (localStorage, гранулярный JSON). */
export interface IDataTableSavedState {
    sorting?: IDataTableSavedSort[];
    columnFilters?: IDataTableSavedFilter[];
    columnVisibility?: Record<string, boolean>;
    columnSizing?: Record<string, number>;
    columnOrder?: string[];
    columnPinning?: IDataTableSavedPinning;
    /** Текущая страница. Нужна для state-в-URL; серверные сохранённые виды
     *  могут по-прежнему её не учитывать. */
    pageIndex?: number;
    pageSize?: number;
    grouping?: string[];
    /** Высота строк. */
    wrapMode?: 'nowrap' | 'trim' | 'wrap';
    /** Стартовая строка поиска (`?search=` из адреса). Только на вход: в снимки состояния
     *  не попадает, чтобы сохранённые виды и состояние в адресе не уносили поиск с собой. */
    globalFilter?: string;
}

const storageKey = (stateKey: string) => `dataTable.state.${stateKey}`;

export function readDataTableState(stateKey: string): IDataTableSavedState | null {
    try {
        const raw = localStorage.getItem(storageKey(stateKey));
        return raw ? (JSON.parse(raw) as IDataTableSavedState) : null;
    } catch {
        return null;
    }
}

export function writeDataTableState(stateKey: string, state: IDataTableSavedState): void {
    try {
        localStorage.setItem(storageKey(stateKey), JSON.stringify(state));
    } catch {
        /* приватный режим/квота — состояние просто не сохранится */
    }
}

// Высота строк живёт отдельным ключом, а не внутри состояния
// таблицы: у серверных списков состоянием владеют сохранённые виды, и класть туда
// личную настройку показа нельзя — она вернулась бы к чужим людям вместе с видом.
const wrapKey = (key: string) => `dataTable.wrap.${key}`;

/** Настройка того же списка, оставшаяся от DX-грида (`grid.settings.<ключ>`). */
const LEGACY_WRAP: Record<number, NonNullable<IDataTableSavedState['wrapMode']>> = {
    0: 'nowrap',
    1: 'trim',
    2: 'wrap',
};

function readLegacyWrapMode(key: string): IDataTableSavedState['wrapMode'] | null {
    try {
        const raw = localStorage.getItem(`grid.settings.${key}`);
        if (!raw) return null;
        const mode = (JSON.parse(raw) as { wordWrapMode?: number }).wordWrapMode;
        return mode == null ? null : (LEGACY_WRAP[mode] ?? null);
    } catch {
        return null;
    }
}

/** Гард значения режима переноса (сериализация: localStorage, сохранённые виды). */
export function isDataTableWrapMode(value: unknown): value is NonNullable<IDataTableSavedState['wrapMode']> {
    return value === 'nowrap' || value === 'trim' || value === 'wrap';
}

export function readWrapMode(key: string | undefined): IDataTableSavedState['wrapMode'] | null {
    if (!key) return null;
    try {
        const raw = localStorage.getItem(wrapKey(key));
        if (isDataTableWrapMode(raw)) return raw;
    } catch {
        return null;
    }
    // список открывается на новом гриде впервые — берём то, что человек выбрал на старом
    return readLegacyWrapMode(key);
}

export function writeWrapMode(key: string | undefined, mode: NonNullable<IDataTableSavedState['wrapMode']>): void {
    if (!key) return;
    try {
        localStorage.setItem(wrapKey(key), mode);
    } catch {
        /* приватный режим/квота — настройка просто не сохранится */
    }
}
